import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { useImmer } from 'use-immer'
import { useCallbackRef } from '@/hooks/useCallbackRef';
import { Sidebar, SidebarButton, SidebarSpacing, Header, CircularMenuButton, Scrollable, Pagination, useIsFormSubmitting, useHandleSubmitError, InlineHeader, InlineSection, SidebarHeaderProvider } from '@/components/DashboardComponents'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal, useIsModalOpen } from '@/components/Modal'
import { useNavigate, useLoaderData, useSubmit, Form, useActionData, useParams } from 'react-router'
import { Button, Textbox, Spacing, HalfSpacing, TextboxGroup, RoundIconButton, RadioGroup, RadioOption, CheckboxGroup, CheckboxOption, Dropdown, DropdownOption, Checkbox, ThreeQuarterSpacing } from '@/form/FormComponents'
import { FormProvider, FormSubmitProvider, FormDataProvider, FormArrayGroup, FormDisabled, useFormData } from '@/form/FormProvider'
import TextareaAutosize from 'react-textarea-autosize';
import { Icon } from '@/components/Icon'
import { createMessage, newConsult, addDifferential, ruleOutDifferential, renameDifferential, concludeEncounter, saveSession, updateMessage } from '@/data/SessionData'
import { useCopyPromptModal } from '@/pages/Scenarios';
import { useIsDeveloper, useUser, refreshUserdata } from '@/systems/Authentication';
import { updateProfile } from '@/data/ProfileData'
import { FileImage } from '@/components/Image';
import { useConversation } from '@elevenlabs/react';
import { publicAction } from '@/data/loaders';
import { useSounds } from '@/hooks/useSounds';
import { useTabFocus } from '@/hooks/useTabFocus';
import { useStorage } from '@/hooks/useStorage';
import { MicVAD } from "@ricky0123/vad-web";
import { useIsMobile, useBreakpoint, isios } from '@/hooks/useIsMobile';

export function SessionLayout() {
  const navigate = useNavigate();
  const is_mobile = useIsMobile();
  const { data, session_id } = useLoaderData();
  const session = data.value;
  const initial_data = data?.value?.data;
  const initial_scenario_data = data?.value?.scenario_data;
  const [scenario_data, updateScenarioData] = useImmer(initial_scenario_data || {});
  const capture_differentials = scenario_data.capture_differentials || false;
  const has_questions = (scenario_data.questions || []).length > 0;
  const has_assessment_and_plan = scenario_data.assessment_and_plan;
  const has_diagnose = scenario_data.diagnose;
  const [considering_differentials, updateConsideringDifferentials] = useImmer(data?.value?.considering_differentials || []);
  const scenario_id = data?.value?.scenario_id;
  const { userdata } = useUser();
  const initial_messages = data?.value?.messages;
  const [messages, updateMessages] = useImmer(initial_messages || []);
  const initial_actors = data?.value?.actors;
  const [actors, updateActors] = useImmer(initial_actors || []);
  const [concluded, setConcluded] = useState(data?.value?.concluded || false);
  const action_data = useActionData();
  const [submitted_skipping_conclude, setSubmittedSkippingConclude] = useState(false);
  const just_submitted = submitted_skipping_conclude || (action_data?.success == true ? action_data.data : false);
  const hasShownSubmittedModal = useRef(false);
  const submitted = data?.value?.submitted || (just_submitted ? true : false) || false;
  const submit = useSubmit();
  const [tab, setTab] = useState((concluded && !submitted) ? (has_diagnose ? "diagnoses" : (has_assessment_and_plan ? "treatment_plan" : "questions")) : "conversation:patient");
  const [conversationMode, setConversationMode] = useState(scenario_data.conversation_mode || "text");
  const conversationModeAllowChange = scenario_data.conversation_mode === "voice" && scenario_data.allow_text_mode;
  const conversationHistoryVisible = scenario_data.conversation_mode === "voice" ? scenario_data.show_transcript : true;
  const submitting = useIsFormSubmitting();
  useHandleSubmitError();
  const { playSound, stopSound } = useSounds(["connect_variant.webm", "mute.webm", "unmute.webm", "disconnect.webm", "connect.webm", "connecting.webm"], conversationMode === "voice");
  const [openErrorModal] = useErrorModal();
  const has_action_pending_interpretation = messages.some(a => a.response_data?.results?.some(r => r.learner_interpret && !r.learner_interpretation));

  let initial_data_diagnoses = {
    diagnoses: considering_differentials.filter(a => a.diagnosis).map(a => {return {diagnosis: a.name, justification: null, why: a.why}}),
    differentials: considering_differentials.filter(a => !a.ruled_out && !a.diagnosis).map(a => {return {differential: a.name, probability_rating: null, justification: null, why: a.why}}),
    differentials_ruled_out: considering_differentials.filter(a => a.ruled_out).map(a => {return {differential: a.name, justification: a.why_ruled_out, why: a.why}}),
  };
  if (initial_data_diagnoses.diagnoses.length == 0) {
    initial_data_diagnoses.diagnoses = [{diagnosis: null, justification: null, why: null}];
  }
  if (initial_data_diagnoses.differentials.length == 0) {
    initial_data_diagnoses.differentials = [{differential: null, probability_rating: null, justification: null, why: null}];
  }
  if (initial_data_diagnoses.differentials_ruled_out.length == 0) {
    initial_data_diagnoses.differentials_ruled_out = [{differential: null, justification: null, why: null}];
  }

  const [conversationActor, setConversationActor] = useState(null);
  const isConnectingOrConnected = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [selectedInputDeviceId, setSelectedInputDeviceId] = useStorage("selectedInputDeviceId", "default");
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useStorage("selectedOutputDeviceId", "default");
  const [activeMicDevice, setActiveMicDevice] = useState(null);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const is_modal_open = useIsModalOpen();
  const is_tab_focused = useTabFocus();

  const voice_conversation_messages = messages.filter((message) => message.to == conversationActor && conversationActor);
  const is_sending_and_obviates_voice = voice_conversation_messages.length > 0 ? !(voice_conversation_messages.slice(-1)[0].response_text || voice_conversation_messages.slice(-1)[0].response_data || voice_conversation_messages.slice(-1)[0].voice) : false;

  const micObviated = ((conversationActor && tab != "conversation:"+conversationActor) || is_modal_open || !is_tab_focused || has_action_pending_interpretation || is_sending_and_obviates_voice) ? true : false;
  const lastMicObviated = useRef(false);

  useEffect(() => {
    if (micObviated !== lastMicObviated.current) {
      lastMicObviated.current = micObviated;
      if (!micMuted && isConnected) {
        if (micObviated) {
          playSound("mute");
        } else {
          playSound("unmute");
        }
      }
    }
  }, [micObviated, playSound, micMuted, isConnected]);

  const vad_strategy = "device"; // "device" or "cloud"
  const vad = useRef(null);

  const stopVAD = useCallback(() => {
    if (vad.current?.destroy) {
      vad.current.destroy();
    }
    vad.current = null;
  }, []);

  const startVAD = useCallback(({ stream, deviceId }) => {
    if (vad_strategy !== "device") {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      return;
    }

    let initiate_date = Date.now();

    if (vad.current?.destroy) {
      vad.current.destroy();
    }

    vad.current = initiate_date;

    if (stream) {
      hasStream(stream);
    } else {
      navigator.mediaDevices.getUserMedia({ audio: (typeof deviceId === "string" ? { deviceId: (isios ? { ideal: deviceId } : { exact: deviceId }) } : true) }).then(function(stream) {

        hasStream(stream);

      }).catch((error) => {
        console.error(error);
      });
    }

    function hasStream(stream) {

      console.log("Starting VAD");

      MicVAD.new({
        onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/",
        baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.27/dist/",
        redemptionMs: 700,
        getStream: () => {
          return stream;
        },
        onSpeechRealStart: () => {
          setUserSpeaking(true);
        },
        onVADMisfire: () => {
          setUserSpeaking(false);
        },
        onSpeechEnd: () => {
          setUserSpeaking(false);
        }
      }).then(function(micvad) {
        if (vad.current === initiate_date) {
          micvad.start();
          vad.current = micvad;
        } else {
          micvad.destroy();
        }
      });

    }

  }, []);

  const vadActive = useRef(false);
  const vadStartTime = useRef(null);
  const vadStopTime = useRef(null);
  
  const handleVadScore = useCallbackRef(({ vadScore }) => {
    if (vad_strategy !== "cloud") {
      return;
    }

    const now = Date.now();
  
    const VAD_ON_THRESHOLD = 0.55;
    const VAD_OFF_THRESHOLD = 0.45;
    const DEBOUNCE_ON_MS = 50;
    const DEBOUNCE_OFF_MS = 100;
  
    // Activation logic (hysteresis + debounce on)
    if (vadScore > VAD_ON_THRESHOLD) {
      if (!vadActive.current) {
        if (!vadStartTime.current) vadStartTime.current = now;
        if (now - vadStartTime.current > DEBOUNCE_ON_MS) {
          vadActive.current = true;
          vadStartTime.current = null;
          vadStopTime.current = null;
          // Speech started!
          setUserSpeaking(true);
        }
      }
    } else if (!vadActive.current) {
      vadStartTime.current = null;
    }
  
    // Deactivation logic (hysteresis + debounce off)
    if (vadActive.current && vadScore < VAD_OFF_THRESHOLD) {
      if (!vadStopTime.current) vadStopTime.current = now;
      if (now - vadStopTime.current > DEBOUNCE_OFF_MS) {
        vadActive.current = false;
        vadStopTime.current = null;
        // Speech stopped!
        setUserSpeaking(false);
      }
    } else if (vadActive.current) {
      vadStopTime.current = null;
    }
  });

  const handleOnConnect = useCallbackRef(() => {
    playSound("connect");
    playSound("connect_variant");
    stopSound("connecting");
    setIsConnected(true);
  });

  const handleOnDisconnect = useCallbackRef(() => {
    setConversationActor(null);
    isConnectingOrConnected.current = false;
    setIsConnected(false);
    playSound("disconnect");
    setMicMuted(false);
    stopVAD();
    setActiveMicDevice(null);
    vadActive.current = false;
    setUserSpeaking(false);
  });

  const handleOnMessage = useCallbackRef(({ source, message }) => {
    if (source === "user") {
      updateMessages(draft => {
        draft.push({
          text: message,
          to: conversationActor,
          voice: true
        })
      });
    } else if (source === "ai") {
      updateMessages(draft => {
        let last_message_index = draft.map((message) => message.to).lastIndexOf(conversationActor);
        if (!draft[last_message_index].response_text) {
          draft[last_message_index].response_text = message;
        } else {
          draft.push({
            text: null,
            to: conversationActor,
            response_text: message,
            voice: true
          })
        }
      })
    }
  });

  const handleOnCorrection = useCallbackRef(({ original_agent_response, corrected_agent_response }) => {
    updateMessages(draft => {
      let last_message_index = draft.map((message) => message.to).lastIndexOf(conversationActor);
      if (draft[last_message_index].response_text === original_agent_response) {
        draft[last_message_index].response_text = corrected_agent_response;
      }
    })
  });

  const handleOnError = useCallbackRef((error) => {
    console.error('Error:', error);
    openErrorModal();
    stopConversation();
  });

  const { startSession, endSession, changeInputDevice, changeOutputDevice, status: conversationStatus } = useConversation({
    // overrides: {
    //   agent: {
    //     prompt: {
    //       prompt: "My custom prompt",
    //     },
    //     firstMessage: "My custom first message",
    //     language: "en",
    //   },
    //   tts: {
    //     voiceId: "custom voice id",
    //   },
    //   conversation: {
    //     textOnly: true,
    //   },
    // },
    preferHeadphonesForIosDevices: true,
    micMuted: micMuted || micObviated,
    onConnect: handleOnConnect,
    onDisconnect: handleOnDisconnect,
    onMessage: handleOnMessage,
    onDebug: (debug) => {
      if (debug.agent_response_correction_event) {
        handleOnCorrection(debug.agent_response_correction_event);
      }
    },
    onError: handleOnError,
    onVadScore: handleVadScore,
  });

  const [openMicPermissionDeniedModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Sapient Clinician is blocked from using your microphone">
        <ModalContent>
          <ol style={{margin: 0, paddingInlineStart: '30px'}}>
            <li style={{marginBottom: '12px'}}>{"Click the site settings button on the left side of your browser's address bar."}</li>
            <li>{"Allow the microphone permission."}</li>
          </ol>
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, []);

  const [openNoMicrophoneErrorModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Please connect a microphone">
        <ModalContent>
          {"We couldn't find a microphone connected to your computer. Please connect a microphone and try again."}
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, []);

  const [openMaxCapacityErrorModal] = useModal(({ closeModal }) => {
    if (conversationModeAllowChange) {
      return (
        <Modal title="All lines are busy. Switch to text mode?">
          <ModalContent>
            {"Voice mode is at full capacity right now due to unusually high usage. Please wait a few minutes and then try again. Thank you for your patience."}
            <br/><br/>
            {"Alternatively, you can switch to text mode to skip the wait."}
          </ModalContent>
          <ModalButtons>
            <Button text="Switch to text mode" className="hollow" onClick={() => {
              setConversationMode("text");
              closeModal();
            }}/>
            <Button text="Dismiss" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    } else {
      return (
        <Modal title="All lines are busy">
          <ModalContent>
            {"We’re at full capacity right now due to unusually high usage. Please wait a few minutes and then try again. Thank you for your patience."}
          </ModalContent>
          <ModalButtons>
            <Button text="Got it" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    }
  }, [conversationModeAllowChange]);

  const [openVoiceDisclaimerModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitVoiceDisclaimer() {
      setLoading(true);

      updateProfile({ voice_disclaimer_accepted: true }).then(function() {
        closeModal();
        refreshUserdata();
        context.doPreConnect();
      }).catch(function(error) {
        closeModal();
        openErrorModal();
        console.error(error);
      });
    }

    return (
      <Modal title="Before you begin">
        <ModalContent>
          {"You are about to talk to a realistic AI bot, not a real human."}
          <br/><br/>
          {"Your conversation will be recorded (even if you can’t see the transcript) and shared with ElevenLabs and the LLM providers that help operate the service. Audio is not saved, and your data is never used to train AI models."}
          <br/><br/>
          {"Your instructor will be able to see your transcript."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="I Understand" className="primary" onClick={submitVoiceDisclaimer}/>
        </ModalButtons>
      </Modal>
    )
  }, [conversationModeAllowChange]);

  const startConversation = useCallback(({ to }) => {

    if ((isConnectingOrConnected.current) && (to == conversationActor)) {
      return;
    }

    if (!navigator.mediaDevices) {
      return openNoMicrophoneErrorModal();
    }

    function doPreConnect() {
      if (isConnectingOrConnected.current) {
        endSession().then(() => {
          if (!isConnectingOrConnected.current) {
            isConnectingOrConnected.current = true;
            doConnect();
          }
        }).catch((error) => {
          console.error(error);
          openErrorModal();
        });
      } else {
        isConnectingOrConnected.current = true;
        doConnect()
      }
    }

    if (!userdata.voice_disclaimer_accepted && !session.demo) {
      openVoiceDisclaimerModal({ doPreConnect });
    } else {
      doPreConnect();
    }

    function doConnect(ignoreDeviceId = false, noDefaultDevice = false) {
      const timePermissionRequested = Date.now();

      const attempted = ((selectedInputDeviceId !== "default" && !ignoreDeviceId) ? selectedInputDeviceId : (noDefaultDevice ? true : "default"));

      navigator.mediaDevices.getUserMedia({ audio: (typeof attempted === "string" ? { deviceId: (isios ? { ideal: attempted } : { exact: attempted }) } : attempted) }).then(function(stream) {

        startVAD({ stream });

        setActiveMicDevice(attempted);

        // const actualDeviceId = stream.getAudioTracks()[0]?.getSettings()?.deviceId;

        setConversationActor(to);

        playSound("connecting", { loop: true });

        publicAction("startVoice", {
          session_id: session_id,
          actor_id: to
        }).then(function({ data }) {

          const signedUrl = data.signed_url;
          const voice_session_secret = data.voice_session_secret;
          const voiceId = data.voiceId;
          
          function attemptStartSession(had_notfound_error = false) {
            startSession({
              signedUrl,
              dynamicVariables: {
                session_id: session_id,
                voice_session_secret: voice_session_secret
              },
              overrides: {
                tts: {
                  voiceId: voiceId,
                }
              },
              inputDeviceId: typeof attempted === "string" ? attempted : null,
              outputDeviceId: had_notfound_error ? null : (selectedOutputDeviceId !== "default" ? selectedOutputDeviceId : null),
            }).then(function() {
  
            }).catch((error) => {
              if (error?.name === "NotFoundError" && !had_notfound_error) {
                attemptStartSession(true);
              } else {
                console.error(error);
                if (error?.reason?.includes("max capacity")) {
                  // reason: This AI agent is currently at max capacity. Please try again later.
                  // code: 4300
                  openMaxCapacityErrorModal();
                } else {
                  openErrorModal();
                }
                setConversationActor(null);
                isConnectingOrConnected.current = false;
                stopVAD();
                setActiveMicDevice(null);
                stopSound("connecting");
              }
            });
          }

          attemptStartSession();

        }).catch((error) => {
          console.error(error);
          openErrorModal();
          setConversationActor(null);
          isConnectingOrConnected.current = false;
          stopVAD();
          setActiveMicDevice(null);
          stopSound("connecting");
        });

      }).catch((error) => {
        console.error(error);
        stopVAD();
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          if (!(error.message.includes("Permission dismissed") || (Date.now() - timePermissionRequested) > 600)) { /* If it takes over 0.6s seconds to get denied, it was probably manual/dismissed, not automatic/persistent */
            openMicPermissionDeniedModal();
          }
          isConnectingOrConnected.current = false;
        } else if (error.name === "ConstraintNotSatisfiedError" || error.name === "OverconstrainedError" || error.name === "NotFoundError") {
          if (attempted === true) {
            openNoMicrophoneErrorModal();
            isConnectingOrConnected.current = false;
          } else if (attempted === "default") {
            doConnect(ignoreDeviceId, true);
          } else {
            doConnect(true);
          }
        } else {
          openErrorModal();
          isConnectingOrConnected.current = false;
        }
      });
    }
    
  }, [endSession, startSession, session_id, openErrorModal, playSound, isConnectingOrConnected, conversationActor, setConversationActor, openMicPermissionDeniedModal, selectedInputDeviceId, selectedOutputDeviceId, openNoMicrophoneErrorModal, startVAD, stopVAD, stopSound, openMaxCapacityErrorModal, session.demo, openVoiceDisclaimerModal, userdata.voice_disclaimer_accepted]);

  const stopConversation = useCallback(() => {
    endSession().then(() => {
      
    }).catch((error) => {
      console.error(error);
      openErrorModal();
    });
  }, [endSession, openErrorModal]);

  const toggleMute = useCallback(() => {
    setMicMuted(!micMuted);
    if (!micMuted) {
      playSound("mute");
    } else {
      playSound("unmute");
    }
  }, [micMuted, playSound]);

  const [mic_devices, setMicDevices] = useState(null);

  const [openMicSettingsModal] = useModal(({ closeModal, context }) => {
    const { inputDevices, outputDevices } = (context.date > (mic_devices?.date ?? 0)) ? context : mic_devices;

    const outputSwitchingSupported = ("setSinkId" in HTMLAudioElement.prototype) && !navigator.vendor?.includes('Apple');

    const selectedInput = (inputDevices.some(a => a.deviceId === selectedInputDeviceId)) ? selectedInputDeviceId : "default";
    const selectedOutput = (outputDevices.some(a => a.deviceId === selectedOutputDeviceId)) ? selectedOutputDeviceId : "default";

    // Safari iOS does not have a "default" device
    let defaultDeviceName = inputDevices.find(a => a.deviceId == "default")?.label?.replace("Default - ", "System default — "); // Chrome Mac, Chrome Win, Firefox Mac
    if (defaultDeviceName === "Default" /*Android*/ || !defaultDeviceName) {
      defaultDeviceName = "System default";
    }

    let inputOptions = [];
    inputOptions.push(<DropdownOption name="default" text={defaultDeviceName} key="default"/>);
    inputOptions = inputOptions.concat(inputDevices.filter(a => a.deviceId !== "default").map((device) => (
      <DropdownOption name={device.deviceId} text={device.label} key={device.deviceId}/>
    )));

    let outputOptions = [];
    const has_default_output = outputDevices.some(a => a.deviceId === "default");
    if (!has_default_output || !outputSwitchingSupported) {
      outputOptions.push(<DropdownOption name="default" text="System default" key="default"/>);
    }
    if (outputSwitchingSupported) {
      outputOptions = outputOptions.concat(outputDevices.map((device) => (
        <DropdownOption name={device.deviceId} text={device.deviceId === "default" ? "System default" : device.label} key={device.deviceId}/>
      )));
    }

    const handleChangeInputDevice = (deviceId) => {
      setSelectedInputDeviceId(deviceId);
      if (conversationStatus === "connected") {
        if (deviceId === "default") {
          if (inputDevices.some(a => a.deviceId === "default")) {
            changeInputDevice({
              inputDeviceId: "default",
            })
            startVAD({ deviceId: "default" });
            setActiveMicDevice("default");
          } else {
            changeInputDevice({});
            startVAD({ deviceId: true });
            setActiveMicDevice(true);
          }
        } else {
          changeInputDevice({
            inputDeviceId: deviceId,
          })
          startVAD({ deviceId });
          setActiveMicDevice(deviceId);
        }
      }
    }

    const handleChangeOutputDevice = (deviceId) => {
      if (!outputSwitchingSupported) {
        return;
      }
      setSelectedOutputDeviceId(deviceId);
      if (conversationStatus === "connected") {
        if (deviceId === "default" && !has_default_output) {
          changeOutputDevice({});
        } else {
          changeOutputDevice({
            outputDeviceId: deviceId,
          })
        }
      }
    }

    return (
      <Modal title="Audio settings">
        <ModalContent>
          <Dropdown title={"Microphone"} value={selectedInput} onChange={handleChangeInputDevice}>
            {inputOptions}
          </Dropdown>
          {outputOptions.length > 1 && <>
            <Spacing/>
            <Dropdown title="Speaker" value={selectedOutput} onChange={handleChangeOutputDevice}>
              {outputOptions}
            </Dropdown>
          </>}
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, [selectedInputDeviceId, setSelectedInputDeviceId, selectedOutputDeviceId, setSelectedOutputDeviceId, conversationStatus, startVAD, mic_devices]);

  const getDevices = () => {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const inputDevices = devices.filter(device => device.kind === 'audioinput').sort((a, b) => {
          if (a.deviceId === 'default') return -1;
          if (b.deviceId === 'default') return 1;
          return 0;
        });
        const outputDevices = devices.filter(device => device.kind === 'audiooutput').sort((a, b) => {
          if (a.deviceId === 'default') return -1;
          if (b.deviceId === 'default') return 1;
          return 0;
        });
        resolve({ inputDevices, outputDevices, date: Date.now() });
      }).catch(reject);
    });
  }

  const openMicSettings = useCallback(() => {
    getDevices().then((result) => {
      openMicSettingsModal(result);
    });
  }, [openMicSettingsModal]);

  useEffect(() => {
    const handleDeviceChange = (event) => {
      getDevices().then((result) => {
        const { inputDevices } = result;
        // When the devices change, there's no way to know if it affects the default input device, so if we are using the default, force a restart to make sure it's using the new default device if it changed. If we were using a specific non-default device, then we could only be affected by the change if the active device was disconnected.
        if (activeMicDevice) {
          if (activeMicDevice === "default" || activeMicDevice === true || (typeof activeMicDevice === "string" && activeMicDevice !== "default" && !inputDevices.some(a => a.deviceId === activeMicDevice))) {

            if (inputDevices.some(a => a.deviceId === "default")) {
              changeInputDevice({
                inputDeviceId: "default",
              })
              startVAD({ deviceId: "default" });
              setActiveMicDevice("default");
            } else {
              changeInputDevice({});
              startVAD({ deviceId: true });
              setActiveMicDevice(true);
            }
  
          }
        }
        setMicDevices(result);
      });
    }

    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
      return () => {
        navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      }
    }
  }, [activeMicDevice, startVAD, changeInputDevice]);

  function doCreateMessage({ text, to, action_type, action_data }) {
    let conversation_message_count = messages.filter(a => a.to == to && !a.voice).length;
    
    updateMessages(draft => {

      draft.push({
        text,
        to,
        action_type,
        action_data
      })
    
    });

    function trySendMessage(retry) {
      if (retry) {
        updateMessages(draft => {
          let last_message_index = draft.map((message) => message.to).lastIndexOf(to);
          delete draft[last_message_index].error_retry_fn;
        })
      }
      createMessage({ session_id, text, to, action_type, action_data, conversation_message_count }).then((data) => {
        updateMessages(draft => {
          let last_message_index = draft.map((message) => message.to).lastIndexOf(to);
          delete draft[last_message_index].error_retry_fn;
          draft[last_message_index].response_text = data.data.response_text;
          draft[last_message_index].response_data = data.data.response_data;
          draft[last_message_index].files = data.data.files;
          draft[last_message_index]._id = data.data._id;
        })
      }).catch((error) => {
        console.error(error);
        if (error.message == "Message count mismatch") {
          navigate(0);
        } else {
          updateMessages(draft => {
            let last_message_index = draft.map((message) => message.to).lastIndexOf(to);
            draft[last_message_index].error_retry_fn = () => trySendMessage(true);
          })
        }
      });
    }
    trySendMessage();
  }

  function handleUpdateMessage({ message_id, response_data }) {
    return new Promise((resolve, reject) => {
      updateMessage({ session_id, message_id, to: "patient", response_data }).then((data) => {
        updateMessages(draft => {
          let message_index = draft.findIndex(a => a._id == message_id);
          draft[message_index] = data.data;
        })
        resolve(data);
      }).catch(reject);
    })
  }

  const [openDiagnosticModal] = useModal(({ closeModal }) => {
    const conversation_messages = messages.filter((message) => message.to == "patient");
    const is_sending = conversation_messages.length > 0 ? !(conversation_messages.slice(-1)[0].response_text || conversation_messages.slice(-1)[0].response_data || conversation_messages.slice(-1)[0].voice) : false;
    
    function submitDiagnosticForm({ formatted_data }) {
      if (is_sending) {
        return;
      }

      closeModal();
      if (formatted_data.related_differentials) {
        formatted_data.related_differentials = formatted_data.related_differentials.map((differential_id) => {
          return {
            name: considering_differentials.find((differential) => differential.id == differential_id).name,
            id: differential_id
          }
        });
      }
      doCreateMessage({ to: "patient", action_type: "diagnostics", action_data: formatted_data })
    }
  
    return (
      <Modal title="Order diagnostic">
        <FormProvider onSubmit={submitDiagnosticForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Lab/imaging to order" placeholder="Complete blood count" required="Please describe what diagnostic to order" />
                  <Spacing/>
                  <Textbox name="justification" title="What are you expecting to find?" required="Please explain what you're expecting to find" placeholder="Low hemoglobin..." multiline={true} />
                  {capture_differentials ? <>
                    <Spacing/>
                    <CheckboxGroup title="Which differentials is this related to?" name="related_differentials" required="Please select at least one of your differentials. Add one if needed.">
                      {considering_differentials.filter(a => !a.ruled_out).map((differential) => (
                        <CheckboxOption name={differential.id} key={differential.id} text={differential.name}/>
                      ))}
                    </CheckboxGroup>
                    <HalfSpacing/>
                    <Button text="Add differential" icon="plus" className="small" onClick={openAddDifferentialModal}/>
                  </> : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  }, [considering_differentials, messages]);

  const [openConsultModal] = useModal(({ closeModal, setLoading }) => {
    function submitConsultForm({ formatted_data, focusError }) {
      setLoading(true);
      newConsult({ session_id, name: formatted_data.name }).then((data) => {
        if (data.data.result == "added") {
          closeModal();
          updateActors(draft => {draft.push(data.data.actor_data)});
          setTab("conversation:"+data.data.actor_data.id);
        } else if (data.data.result == "duplicate") {
          closeModal();
          setTab("conversation:"+data.data.actor_id);
        } else { // result == "invalid"
          setLoading(false);
          focusError({name: "name", select: true, error: "This person isn't available to consult with."});
        }
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Consult for advice">
        <FormProvider onSubmit={submitConsultForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Who do you want to consult with?" placeholder="Cardiologist" onEnter={handleSubmit} required="Please enter who you want to consult with" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  const [openInterventionModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Real-time intervention">
        <ModalContent>
          This feature isn't available for testing yet.
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [openConcludeModal] = useModal(({ closeModal, setLoading }) => {
    function submitConcludeEncounter() {
      if (considering_differentials.filter(a => !a.ruled_out).length > 0) {
        closeModal();
        openPromoteDifferentialsModal();
      } else {
        setLoading(true);

        doConcludeEncounter({}).then(() => {
          closeModal();
        }).catch((error) => {
          console.error(error);
          closeModal();
          openErrorModal();
        });
      }
    }

    return (
      <Modal title="Conclude patient encounter?">
        <ModalContent>
          {/* Please make sure you have summarized the assessment and plan with the patient. After continuing, you will no longer be able to interact with the patient. */}
          Make sure you're done interacting with the patient.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button className="primary" text="Conclude encounter" onClick={submitConcludeEncounter}/>
        </ModalButtons>
      </Modal>
    )
  })

  function doConcludeEncounter({ differential_ids = [] }) {
    return new Promise((resolve, reject) => {
      concludeEncounter({ session_id, differential_ids }).then(({ data }) => {

        setConcluded(true);

        if (data.value) {

          const { questions, diagnose, assessment_and_plan } = data.value.scenario_data;

          updateScenarioData(draft => {
            draft.questions = questions;
            draft.diagnose = diagnose;
            draft.assessment_and_plan = assessment_and_plan;
          });
          updateConsideringDifferentials(draft => {
            draft = draft.map((differential) => {
              if (differential_ids.includes(differential.id)) {
                differential.diagnosis = true;
              }
              return differential;
            });
          });

          setTab(diagnose ? "diagnoses" : (assessment_and_plan ? "treatment_plan" : "questions"));
        } else {
          setSubmittedSkippingConclude(data);
        }

        stopConversation();

        resolve();

      }).catch((error) => {
        reject(error);
      });
    });
  }

  const [openPromoteDifferentialsModal] = useModal(({ closeModal, setLoading }) => {
    function submitDifferentials({ formatted_data }) {
      setLoading(true);

      let differential_ids = formatted_data ? (formatted_data.differentials_none ? [] : formatted_data.differentials) : [];
      doConcludeEncounter({ differential_ids }).then(() => {
        closeModal();
      }).catch((error) => {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Should any differentials become diagnoses?">
        <FormProvider onSubmit={submitDifferentials}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <FormDataProvider fields={["differentials_none"]}>{({data}) => (
                    <FormDisabled obviated={data.differentials_none}>
                      <CheckboxGroup name="differentials">
                        {considering_differentials.filter(a => !a.ruled_out).map((differential) => (
                          <CheckboxOption name={differential.id} key={differential.id} text={differential.name}/>
                        ))}
                      </CheckboxGroup>
                    </FormDisabled>
                  )}</FormDataProvider>
                  <Checkbox name="differentials_none" text="None of the above"/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Skip" onClick={() => submitDifferentials({})}/>
                  <Button className="primary" text="Continue" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function submitForm({ formatted_data }) {
    submit(formatted_data, { method: "post", encType: "application/json" });
  }

  function handleValidateFailed({ element }) {
    setTab(element.closest("[tabname]").getAttribute("tabname"));
  }

  const reviewFeedback = useCallback(() => {
    if (!session.preview) {
      navigate("/courses/"+session.course_id+"/assessments/"+session.assessment_id+"/review", { replace: true });
    } else {
      navigate("/session/"+session_id+"/review", { replace: true });
    }
  }, [session.preview, session.course_id, session.assessment_id, session_id, navigate]);

  const [openSubmittedModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal width={330} confetti={true}>
        <ModalContent>
          <div className="assignment_submitted">
            <Icon name="check-circle"/>
            <div>Assessment submitted</div>
            <div>{context.returned ? "Feedback will be available in a few minutes." : "You'll get an email once feedback is available."}</div>
          </div>
        </ModalContent>
        <ModalButtons centered={true}>
          {context.returned ?
            <Button text="Continue" className="primary" onClick={() => {closeModal();}} /> 
          :
            <Button text={"Return to "+(session.assessment_id ? "course" : "scenarios")} onClick={() => {closeModal(); goBackHome();}}/>
          }
          {/* {context.returned ?
            <Button icon="comment-text-outline" className="primary" text="View feedback" onClick={() => {closeModal(); reviewFeedback();}} /> 
          :
            <Button icon="clipboard-text-search-outline" text="Review assessment" onClick={() => {closeModal(); reviewFeedback();}} /> 
          }
          <Button text={"Return to "+(session.assessment_id ? "course" : "scenarios")} onClick={() => {closeModal(); goBackHome();}}/> */}
        </ModalButtons>
      </Modal>
    )
  });

  useEffect(() => {
    if (just_submitted && !hasShownSubmittedModal.current) {
      openSubmittedModal({ returned: just_submitted.returned }, { pathnames: ["/courses/"+session.course_id+"/assessments/"+session.assessment_id+"/review", "/session/"+session_id+"/review"] });
      hasShownSubmittedModal.current = true;
      if (just_submitted.returned) {
        reviewFeedback();
      }
    }
  }, [just_submitted, openSubmittedModal, reviewFeedback, session.course_id, session.assessment_id, session_id])

  function goBackHome() {
    if (session.course_id) {
      navigate("/courses/"+session.course_id);
    } else {
      navigate("/scenarios");
    }
  }

  const [openPhysicalExamRoutineModal] = useModal(({ closeModal }) => {
    const conversation_messages = messages.filter((message) => message.to == "patient");
    const is_sending = conversation_messages.length > 0 ? !(conversation_messages.slice(-1)[0].response_text || conversation_messages.slice(-1)[0].response_data || conversation_messages.slice(-1)[0].voice) : false;

    function submitPhysicalExamRoutineForm({ formatted_data }) {
      if (is_sending) {
        return;
      }

      closeModal();
      if (formatted_data.working_diagnosis) {
        formatted_data.working_diagnosis = {
          name: considering_differentials.find((differential) => differential.id == formatted_data.working_diagnosis).name,
          id: formatted_data.working_diagnosis
        };
      }
      doCreateMessage({ to: "patient", action_type: "physical_exam_routine", action_data: formatted_data })
    }

    return (
      <Modal title="Routine physical exam">
        <FormProvider onSubmit={submitPhysicalExamRoutineForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="justification" title="What are you expecting to find?" placeholder="Tachycardia..." required="Please explain what you're expecting to find" multiline={true} />
                  {capture_differentials ? <>
                    <Spacing/>
                    <RadioGroup title="What's your initial working diagnosis?" name="working_diagnosis" required="Please select one of your differentials. Add one if needed.">
                      {considering_differentials.filter(a => !a.ruled_out).map((differential) => (
                        <RadioOption name={differential.id} key={differential.id} text={differential.name}/>
                      ))}
                    </RadioGroup>
                    <HalfSpacing/>
                    <Button text="Add differential" icon="plus" className="small" onClick={openAddDifferentialModal}/>
                  </> : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  }, [considering_differentials, messages]);

  const [openPhysicalExamTestModal] = useModal(({ closeModal }) => {
    const conversation_messages = messages.filter((message) => message.to == "patient");
    const is_sending = conversation_messages.length > 0 ? !(conversation_messages.slice(-1)[0].response_text || conversation_messages.slice(-1)[0].response_data || conversation_messages.slice(-1)[0].voice) : false;

    function submitPhysicalExamTestForm({ formatted_data }) {
      if (is_sending) {
        return;
      }

      closeModal();
      if (formatted_data.related_differentials) {
        formatted_data.related_differentials = formatted_data.related_differentials.map((differential_id) => {
          return {
            name: considering_differentials.find((differential) => differential.id == differential_id).name,
            id: differential_id
          }
        });
      }
      doCreateMessage({ to: "patient", action_type: "physical_exam_test", action_data: formatted_data })
    }

    return (
      <Modal title="Physical exam test">
        <FormProvider onSubmit={submitPhysicalExamTestForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Physical exam test to perform" placeholder="McBurney's point tenderness" required="Please describe what physical exam test to perform" />
                  <Spacing/>
                  <Textbox name="justification" title="What are you expecting to find?" placeholder="Pain to palpation..." required="Please explain what you're expecting to find" multiline={true} />
                  {capture_differentials ? <>
                    <Spacing/>
                    <CheckboxGroup title="Which differentials is this related to?" name="related_differentials" required="Please select at least one of your differentials. Add one if needed.">
                      {considering_differentials.filter(a => !a.ruled_out).map((differential) => (
                        <CheckboxOption name={differential.id} key={differential.id} text={differential.name}/>
                      ))}
                    </CheckboxGroup>
                    <HalfSpacing/>
                    <Button text="Add differential" icon="plus" className="small" onClick={openAddDifferentialModal}/>
                  </> : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  }, [considering_differentials, messages]);

  const has_run_routine_physical_exam = messages.find((message) => message.to == "patient" && message.action_type == "physical_exam_routine") ? true : false;
  function handlePhysicalExam() {
    if (has_run_routine_physical_exam) {
      openPhysicalExamTestModal();
    } else {
      openPhysicalExamRoutineModal();
    }
  }

  const messages_with_action_hints = messages.map((message) => {
    if (message.action_type == "physical_exam_routine" && message.response_data) {
      return {...message, action_hint: { text: "Perform a physical exam test to investigate further", icon: "human-search" }};
    }
    return message;
  })

  const [openAddDifferentialModal] = useModal(({ closeModal, setLoading }) => {
    function submitAddDifferentialForm({ formatted_data, focusError }) {
      setLoading(true);
      addDifferential({ session_id, name: formatted_data.name, why: formatted_data.why }).then((data) => {
        updateConsideringDifferentials(draft => {
          draft.push(data.data.differential_data);
        });
        closeModal();
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Add differential">
        <FormProvider onSubmit={submitAddDifferentialForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Differential diagnosis" placeholder="Pneumonia" required="Please name the differential diagnosis" />
                  <Spacing/>
                  <Textbox name="why" title="What's making you consider this?" placeholder="Because..." multiline={true} required="Please explain your thinking" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  const [openRuleOutDifferentialModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitModalForm({ formatted_data, focusError }) {
      setLoading(true);
      ruleOutDifferential({ session_id, id: context.differential_id, why: formatted_data.why }).then((data) => {
        updateConsideringDifferentials(draft => {
          let ruled_out_item = draft.find((differential) => differential.id == context.differential_id);
          ruled_out_item.ruled_out = true;
          ruled_out_item.why_ruled_out = formatted_data.why;
        });
        closeModal();
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "why", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title={`Rule out ${considering_differentials.find((differential) => differential.id == context.differential_id).name}`}>
        <FormProvider onSubmit={submitModalForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="why" title="What made you rule this out?" placeholder="Because..." multiline={true} required="Please explain your thinking" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  const [openRenameDifferentialModal] = useModal(({ context, closeModal, setLoading }) => {
    const old_name = considering_differentials.find((differential) => differential.id == context.differential_id).name;

    function submitModalForm({ formatted_data, focusError }) {

      if (formatted_data.name == old_name) {
        focusError({name: "name", error: "Differential hasn't changed"})
        return;
      }

      setLoading(true);
      renameDifferential({ session_id, id: context.differential_id, name: formatted_data.name, why: formatted_data.why }).then((data) => {
        updateConsideringDifferentials(draft => {
          let ruled_out_item = draft.find((differential) => differential.id == context.differential_id);
          ruled_out_item.name = formatted_data.name;
        });
        closeModal();
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Change differential">
        <FormProvider onSubmit={submitModalForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" default_value={old_name} title="Differential diagnosis" required="Please name the differential diagnosis" />
                  <Spacing/>
                  <Textbox name="why" title="What made you change this differential?" multiline={true} />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  function handleAutosave({ formatted_data }) {
    return saveSession({ session_id, data: formatted_data });
  }

  const [openDemoWelcomeModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Welcome to the Example Case">
        <ModalContent>
          In this scenario, your objective is to diagnose the AI patient and close the loop.
          <br/><br/>
          When you're done talking to the AI patient, click the <div style={{display: "inline-block", backgroundColor: "var(--accentprimary)", padding: "7px 9px", borderRadius: "40px", fontSize: "13px", fontWeight: "500", color: "white", verticalAlign: "bottom", marginTop: "3px", marginBottom: "-4px", marginRight: "2px"}}>{is_mobile ? "Conclude" : "Conclude encounter"}</div> button in the top right corner.
          <br/><br/>
          After submitting the assessment, you'll get feedback on how you did.
          <br/><br/>
          <span style={{fontSize: "16px", color: "var(--btext)"}}>By continuing, you agree to our <a href="https://sapientclinician.com/privacy" target="_blank" rel="noopener noreferrer" className="links">Privacy Policy</a> and <a href="https://sapientclinician.com/terms" target="_blank" rel="noopener noreferrer" className="links">Terms of Use</a>. Your conversation will be recorded and shared with ElevenLabs and LLM providers that help operate the service. Audio is not saved, and your data is never used to train AI models.</span>
        </ModalContent>
        <ModalButtons>
          <Button text="Continue" className="primary" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const hasShownDemoWelcomeModal = useRef(false);

  useEffect(() => {
    if (session.demo && messages.length == 0 && !hasShownDemoWelcomeModal.current) {
      hasShownDemoWelcomeModal.current = true;
      openDemoWelcomeModal();
    }
  }, [session.demo, messages.length, openDemoWelcomeModal]);

  return (
    <>
      <div className="dashboard_container">
        <header className="dashboard_topbar">
          <div className="topbar_left">
            <CircularMenuButton onClick={() => navigate(session.demo ? -2 : -1)} icon="arrow-left"/>
            {
              session.preview ? (
                session.assessment_id ? <>
                  <div className="topbar_title">Preview assessment</div>
                </> : <>
                  <div className="topbar_title">Preview scenario</div>
                  {userdata.email == "demo@sapientclinician.com" && (
                    <div className="topbar_button">
                      <Button text="Restart demo" className="hollow" icon="refresh" onClick={() => navigate("/scenarios/"+scenario_id+"/preview", { replace: true })}/>
                    </div>
                  )}
                </>
              ) : (
                <div className="topbar_title">{session.assessment_data?.title || "Assessment"}</div>
              )
            }
          </div>
          <div className="topbar_right">
            {!concluded && <div className="topbar_button">
              <Button className="primary" text={is_mobile ? "Conclude" : "Conclude encounter"} onClick={openConcludeModal}/>
            </div>}
          </div>
        </header>
        <div className="dashboard_main" id="session_main">
          <SidebarHeaderProvider>
            <Sidebar>
              <SidebarButton onClick={() => {setTab("conversation:patient")}} icon="account" name="Patient" selected={tab == "conversation:patient"} big={true} outlined={conversationActor == "patient" && conversationStatus === "connected"}/>
              {actors.map((actor_data) => <SidebarButton key={actor_data.id} onClick={() => {setTab("conversation:"+actor_data.id)}} icon="doctor" name={actor_data.name} selected={tab == "conversation:"+actor_data.id} big={true} outlined={conversationActor == actor_data.id && conversationStatus === "connected"}/>)}
              {(!concluded && session.scenario_data.consult_for_advice) ? <SidebarButton onClick={openConsultModal} icon="account-plus" name="Consult for advice" selected={false} big={true}/> : null}
              {concluded ? <>
                <SidebarSpacing/>
                {has_diagnose ? <SidebarButton onClick={() => {setTab("diagnoses")}} name="Diagnoses" selected={tab == "diagnoses"} big={true}/> : null}
                {has_assessment_and_plan ? <SidebarButton onClick={() => {setTab("treatment_plan")}} name="Assessment and plan" selected={tab == "treatment_plan"} big={true}/> : null}
                {has_questions ? <SidebarButton onClick={() => {setTab("questions")}} name="Follow-up questions" selected={tab == "questions"} big={true}/> : null}
              </> : null}
            </Sidebar>
            <Scrollable className={"dashboard_content" + (tab.startsWith("conversation:") ? " no_header" : "")} depencencies={[tab]}>
              {(is_mobile && !concluded && (actors.length > 1 || session.scenario_data.consult_for_advice || capture_differentials)) && <Header tabs={[
                {id: "conversation:patient", name: "Patient", icon: "account"},
                ...actors.map((actor_data) => ({id: "conversation:"+actor_data.id, name: actor_data.name, icon: "doctor"})),
                ...((!concluded && session.scenario_data.consult_for_advice) ? [{id: "consult_for_advice", name: "Consult", icon: "account-plus", onClick: openConsultModal}] : []),
                ...(capture_differentials ? [{id: "differentials", name: "Differentials", icon: "assignment"}] : [])
              ]} activeTab={tab} setActiveTab={setTab}/>}
              <Conversation actor_data={{id: "patient", name: "Patient", opening_blurb: scenario_data.opening_blurb, workup_to_date_summary: scenario_data.workup_to_date_summary}} message_box_disabled={has_action_pending_interpretation} {...{messages: messages_with_action_hints, doCreateMessage, handleUpdateMessage, tab, concluded, is_assessment: session.assessment_id, is_demo: session.demo, conversationStatus, startConversation, stopConversation, micMuted, micObviated, toggleMute, conversationActor, conversationMode, setConversationMode, conversationModeAllowChange, conversationHistoryVisible, openMicSettings, userSpeaking }}>
                {scenario_data.allow_physical_exam ? (has_run_routine_physical_exam ? <Button text="Physical exam test" icon="human-search" className="small" onClick={handlePhysicalExam} disabled={has_action_pending_interpretation}/> : <Button text="Routine physical exam" icon="human-handsdown" className="small" onClick={handlePhysicalExam} disabled={has_action_pending_interpretation}/>) : null}
                {scenario_data.allow_diagnostics ? <Button text="Diagnostic" icon="test-tube" className="small" onClick={openDiagnosticModal} disabled={has_action_pending_interpretation}/> : null}
                {/* <Button text="Intervention" icon="needle" className="small" onClick={openInterventionModal}/> */}
              </Conversation>
              {actors.map((actor_data) => <Conversation actor_data={actor_data} key={actor_data.id} {...{messages: messages_with_action_hints, doCreateMessage, handleUpdateMessage, tab, concluded, is_assessment: session.assessment_id, is_demo: session.demo, conversationStatus, startConversation, stopConversation, micMuted, micObviated, toggleMute, conversationActor, conversationMode: "text", conversationModeAllowChange: false, conversationHistoryVisible: true, openMicSettings, userSpeaking }}/>)}
              <Form method="post" onSubmit={(event) => event.preventDefault()}>
                {concluded ? <>
                  <FormProvider onSubmit={submitForm} onValidateFailed={handleValidateFailed} initial_data={initial_data || initial_data_diagnoses} readOnly={submitted} unsaved_modal={submitted ? false : "session"} handleAutosave={handleAutosave} autosave_enabled={!submitted && !submitting}>
                      {has_diagnose ? <Diagnoses visible={tab == "diagnoses"} considering_differentials={considering_differentials} {...{scenario_data}}/> : null}
                      {has_assessment_and_plan ? <TreatmentPlan visible={tab == "treatment_plan"} {...{scenario_data}}/> : null}
                      {has_questions ? <FollowUpQuestions visible={tab == "questions"} {...{scenario_data}}/> : null}
                      {
                        ["diagnoses", "treatment_plan", "questions"].includes(tab) ? 
                          <Pagination show_continue={!submitted && tab !== (has_questions ? "questions" : (has_assessment_and_plan ? "treatment_plan" : "diagnoses"))} getContainerElement={() => document.querySelector("#session_main")} autosave_indicator={!submitted}>
                            {(concluded && !submitted) ? <FormSubmitProvider>{({handleSubmit}) => <Button className="primary" text="Submit assessment" onClick={handleSubmit} disabled={submitting}/>}</FormSubmitProvider> : null}
                          </Pagination>
                        : null
                      }
                  </FormProvider>
                </> : null}
              </Form>
              {tab == "differentials" && <div className="dashboard_content_inner nearest_scrollable_container">
              <div className="dashboard_content_container"><Differentials considering_differentials={considering_differentials} openRenameDifferentialModal={openRenameDifferentialModal} openRuleOutDifferentialModal={openRuleOutDifferentialModal} openAddDifferentialModal={openAddDifferentialModal}/></div></div>}
            </Scrollable>
          </SidebarHeaderProvider>
          {(capture_differentials && !concluded && !is_mobile) && (
            <Scrollable className="dashboard_side_panel">
              <Header title="Differentials" className="mini"/>
              <div className="dashboard_side_panel_inner nearest_scrollable_container">
                <Differentials considering_differentials={considering_differentials} openRenameDifferentialModal={openRenameDifferentialModal} openRuleOutDifferentialModal={openRuleOutDifferentialModal} openAddDifferentialModal={openAddDifferentialModal}/>
              </div>
            </Scrollable> 
          )}
        </div>
      </div>
    </>
  )
}

function Differentials({ considering_differentials, openRenameDifferentialModal, openRuleOutDifferentialModal, openAddDifferentialModal }) {
  return (
    <>
      <div className="considering_differentials_list">
        {considering_differentials.filter(a => !a.ruled_out).length > 0 ? (
          considering_differentials.filter(a => !a.ruled_out).map((differential) => (
            <div className="considering_differential_item" key={differential.id}>
              <div>{differential.name}</div>
              <div><RoundIconButton icon="pencil-outline" className="small small_icon" onClick={() => {openRenameDifferentialModal({differential_id: differential.id})}} /><RoundIconButton icon="close" className="small" onClick={() => {openRuleOutDifferentialModal({differential_id: differential.id})}} /></div>
            </div>
          ))
        ) : (
          <div className="considering_differentials_list_placeholder">
            <div>Add any differentials that you're considering.</div>
            <div>Remove any that you rule out.</div>
          </div>
        )}
      </div>
      <Button text="Add differential" icon="plus" className="small" onClick={openAddDifferentialModal}/>
      <Spacing/>
    </>
  )
}

function Conversation({ actor_data, messages, doCreateMessage, message_box_disabled, children, tab, concluded, is_assessment, is_demo, handleUpdateMessage, conversationStatus, startConversation, stopConversation, micMuted, micObviated, toggleMute, conversationActor, conversationMode, setConversationMode, conversationModeAllowChange, conversationHistoryVisible, openMicSettings, userSpeaking }) {
  const { id: actor_id, name } = actor_data;
  const scroller_ref = useRef();
  const last_message_ref = useRef();
  const message_box_ref = useRef();
  const [last_scroll_pos, setLastScrollPos] = useState(0);  

  const visible = tab == "conversation:"+actor_id;
  const conversation_messages = messages.filter((message) => message.to == actor_id);
  const conversation_message_items = (actor_id == "patient" ? [{type: "opening_blurb", opening_blurb: actor_data.opening_blurb, workup_to_date_summary: actor_data.workup_to_date_summary}] : []).concat(conversation_messages.filter(a => !a.voice || conversationHistoryVisible || conversationMode === "text").map(a => {return {type: "message", message: a}}));

  const empty_conversation_hint = actor_id == "patient" && conversation_messages.length == 0;

  const [just_sent_message, setJustSentMessage] = useState(false);
  function sendConversationMessage({text}) {
    doCreateMessage({text, to: actor_id});
    setJustSentMessage(true);
  }

  useEffect(() => {
    if (visible) {
      if (message_box_ref.current) {
        message_box_ref.current.focus();
      }
    } else {
      setJustSentMessage(false);
    }
  }, [visible])

  const is_sending = conversation_messages.length > 0 ? !(conversation_messages.slice(-1)[0].response_text || conversation_messages.slice(-1)[0].response_data || conversation_messages.slice(-1)[0].voice) : false;

  useEffect(() => {

    let scrolled_up_amount = scroller_ref.current.scrollHeight - scroller_ref.current.scrollTop - scroller_ref.current.clientHeight;

    if (scrolled_up_amount < 2) {
      scroller_ref.current.scrollTop = last_scroll_pos;
    }

    last_message_ref.current.scrollIntoView({behavior: "smooth", });

    setJustSentMessage(true);
    
  }, [conversation_message_items.length])

  const toggleConversationMode = useCallback(() => {
    setConversationMode(conversationMode === "voice" ? "text" : "voice");
  }, [conversationMode, setConversationMode]);

  const banner_visible = is_assessment && !is_demo && !concluded && actor_id == "patient" && conversation_message_items.length == 1 && conversation_message_items[0].type == "opening_blurb" && conversationMode !== "voice";

  const is_dev = useIsDeveloper();
  const { session_id } = useParams();

  const is_mobile = useIsMobile();
  const move_switch_to_text_button = useBreakpoint((width) => width <= 800);

  const has_left_voice_toggle = (conversationModeAllowChange && conversationMode === "text");
  const has_right_voice_toggle = (conversationModeAllowChange && conversationMode === "voice" && move_switch_to_text_button && !(conversationStatus === "connected" && conversationActor == actor_id));
  const has_actions = (children || has_left_voice_toggle || has_right_voice_toggle) && !concluded;

  return (
    <div style={{display: visible ? "contents" : "none"}}>
      {/* <Header title={name}>
        {(is_dev && actor_id == "patient") ? <SaveTranscriptButton conversation_message_items={conversation_message_items} session_id={session_id}/> : null}
      </Header> */}
      {(is_mobile && concluded) && <Header title={name}/>}
      <div className="conversation_messages_container">
        <div className={"conversation_messages_container_inner" + (banner_visible ? " has_banner" : "") + (actor_id !== "patient" ? " color_advice" : "")} ref={scroller_ref} onScroll={() => setLastScrollPos(scroller_ref.current.scrollTop)}>
          <div className={"conversation_message_actions_background" + (has_actions ? " has_actions" : "")}></div>
          {has_actions && (
            <div className={"conversation_message_actions" + (conversationMode === "voice" ? " with_voice" : "")}>
              <div className="conversation_message_actions_inner_left">
                {has_left_voice_toggle && <Button icon="microphone" text="Switch to voice" className="small hollow" onClick={toggleConversationMode}/>}
              </div>
              <div className="conversation_message_actions_inner">
                {has_right_voice_toggle ? <Button icon="comment-text-outline" text="Switch to text" className="small hollow" onClick={toggleConversationMode} disabled={conversationActor == actor_id}/> : null}
                {children}
              </div>
            </div>
          )}
          {banner_visible && <div className="conversation_information_banner">
            <div>
              <div>
                <Icon name="eye-outline"/>
              </div>
              <div>Your instructor will see this conversation.</div>
            </div>
          </div>}
          <div className="conversation_message_history">
            {conversation_message_items.length > 1 ? conversation_message_items.slice(0, -1).map((item, index) => <MessageItem key={index} item={item} review={false} handleUpdateMessage={handleUpdateMessage}/>) : null}
          </div>
          <div className="conversation_message_last" ref={last_message_ref}>
            <div className="conversation_message_last_inner">
              {conversation_message_items.length > 0 ? <MessageItem item={conversation_message_items.slice(-1)[0]} review={false} message_appear={just_sent_message} key={conversation_message_items.length+1} handleUpdateMessage={handleUpdateMessage}/> : null}
            </div>
          </div>
        </div>
      </div>
      {!concluded ? 
        <div className="conversation_message_area">
          {conversationMode === "text" ? (
            <div className="conversation_message_box">
              <MessageBox onSend={sendConversationMessage} is_sending={is_sending} ref={message_box_ref} name={name} disabled={message_box_disabled} empty_conversation_hint={empty_conversation_hint} toggleConversationMode={toggleConversationMode} conversationModeAllowChange={conversationModeAllowChange} />
            </div>
          ) : (
            <VoiceControls conversationStatus={conversationStatus} startConversation={startConversation} actor_id={actor_id} stopConversation={stopConversation} micMuted={micMuted} micObviated={micObviated} toggleMute={toggleMute} name={name} conversationActor={conversationActor} toggleConversationMode={toggleConversationMode} conversationModeAllowChange={conversationModeAllowChange} openMicSettings={openMicSettings} userSpeaking={userSpeaking}/>
          )}
        </div>
      : null}
    </div>
  )
}

const MessageBox = forwardRef(function MessageBox({initial_value, onSend, is_sending, name, disabled, empty_conversation_hint}, ref) {
  const [value, setValue] = useState(initial_value || "");

  const is_mobile = useIsMobile();
  const is_touch = window.matchMedia?.('(pointer: coarse)').matches ?? false;

  function handleSend() {
    if (value.trim().length > 0 && !is_sending && !disabled) {
      onSend({text: value.trim()});
      setValue("");
    }
  }

  return (
    <div className={`message_box ${disabled ? "disabled" : ""}`} inert={disabled ? "true" : null}>
      <TextareaAutosize
        id="message_input"
        ref={ref}
        type="text"
        placeholder={empty_conversation_hint ? "Introduce yourself..." : `Say something to the ${name.toLowerCase()}...`}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        onKeyDown={(event) => {

          if (event.key == 'Enter' && !event.shiftKey && !(is_mobile && is_touch)) {
            handleSend();
            event.preventDefault();
            event.stopPropagation();
          }

        }}
        value={value}
        minRows={1}
        maxRows={8}
      /> 
      <div className="send_button_container">
        <div className="send_button" onClick={handleSend} tabIndex={0} role='button' aria-label='Send message'>
          <Icon name="send-variant"/>
        </div>
      </div>
    </div>
  )
})

const VoiceControls = ({conversationStatus, startConversation, actor_id, stopConversation, micMuted, micObviated, toggleMute, name, conversationActor, toggleConversationMode, conversationModeAllowChange, openMicSettings, userSpeaking }) => {
  // is speaking indicator
  // input & output switcher

  const move_switch_to_text_button = useBreakpoint((width) => width <= 800);

  return (
    <div className="conversation_voice_controls">
      {(conversationStatus === "connected" && conversationActor == actor_id) ? (
        <>
          <div className="side"></div>
          <div className="middle">
            <Button icon="cog-outline" className="round medium" onClick={openMicSettings}/>
            <Button key="toggle_mute" icon={(micMuted || micObviated) ? "microphone-off" : "microphone"} className={`mic_button round large ${(micMuted || micObviated) ? "destructive" : "primary"} ${(userSpeaking && !micMuted && !micObviated) ? "mic_glow" : ""}`} onClick={toggleMute} disabled={micObviated}/>
            <Button icon="close" className="round medium" onClick={stopConversation}/>
          </div>
          <div className="side"></div>
        </>
      ) : (
        <>
          <div className="side">
            {(conversationModeAllowChange && !move_switch_to_text_button) ? <Button icon="comment-text-outline" text="Switch to text" className="small hollow" onClick={toggleConversationMode} disabled={conversationActor == actor_id}/> : null}
          </div>
          <div className="middle">
            <Button key="toggle_mute" icon="microphone" text={`Talk to ${name.toLowerCase()}`} loading={conversationActor == actor_id} className="primary large" onClick={() => startConversation({to: actor_id})}/>
          </div>
          <div className="side"></div>
        </>
      )}
    </div>
  )
}

function MessageBubble({text, direction, message_appear, voice}) {
  return (
    <div className={"message " + direction + (message_appear ? (" message_appear" + (direction == "to" ? " message_appear_delay" : "")) : "") + ((voice && direction == "to") ? " voice_message" : "")}>
      <div className="message_inner">
        <div className="message_text">{(voice && direction == "to") ? ('“'+text+'”') : text}</div>
      </div>
    </div>
  )
}

function ActionTo({action_type, action_data, message_appear, review}) {
  return (
    <div className={"message action action_to" + (message_appear ? " message_appear message_appear_delay" : "")}>
      <div className="message_inner">
        <div className="message_action_header">
          <div>
            <Icon name={{"diagnostics":"test-tube","physical_exam_routine":"human-handsdown","physical_exam_test":"human-search"}[action_type]}/>
          </div>
          <div>{{"diagnostics":"Diagnostic","physical_exam_routine":"Routine physical exam","physical_exam_test":"Physical exam test"}[action_type]}</div>
        </div>
        {action_type == "diagnostics" ? <>
          <div className="message_text">{action_data.name}</div>
          {review ? <>
            <div className="message_action_subheader">Expecting to find?</div>
            <div className="message_text">{action_data.justification}</div>
            {action_data.related_differentials ? <>
              <div className="message_action_subheader">Related differentials</div>
              <div className="message_text">{action_data.related_differentials.map(a => a.name).join(", ")}</div>
            </> : null}
          </> : null}
        </> : null}
        {action_type == "physical_exam_routine" ? <>
          {review ? <>
            <div className="message_action_subheader">Expecting to find?</div>
            <div className="message_text">{action_data.justification}</div>
            {action_data.working_diagnosis ? <>
              <div className="message_action_subheader">Working diagnosis</div>
              <div className="message_text">{action_data.working_diagnosis.name}</div>
            </> : null}
          </> : null}
        </> : null}
        {action_type == "physical_exam_test" ? <>
          <div className="message_text">{action_data.name}</div>
          {review ? <>
            <div className="message_action_subheader">Expecting to find?</div>
            <div className="message_text">{action_data.justification}</div>
            {action_data.related_differentials ? <>
              <div className="message_action_subheader">Related differentials</div>
              <div className="message_text">{action_data.related_differentials.map(a => a.name).join(", ")}</div>
            </> : null}
          </> : null}
        </> : null}
      </div>
    </div>
  )
}

function ActionFrom({action_type, action_data, response_text, response_data, files, message_appear, handleUpdateMessage, message_id}) {

  return (
    <div className={"message action action_from" + (message_appear ? " message_appear" : "")}>
      <div className="message_inner">
        {action_type == "physical_exam_routine" ? <>
          {response_data.vitals ? <>
            <div className="message_action_subheader">Vitals and Measurements</div>
            <div className="message_text">{response_data.vitals}</div>
          </> : null}
          {response_data.general_appearance ? <>
            <div className="message_action_subheader">General appearance</div>
            <div className="message_text">{response_data.general_appearance}</div>
          </> : null}
          {response_data.heent ? <>
            <div className="message_action_subheader">HEENT</div>
            <div className="message_text">{response_data.heent}</div>
          </> : null}
          {response_data.lungs ? <>
            <div className="message_action_subheader">Lungs</div>
            <div className="message_text">{response_data.lungs}</div>
          </> : null}
          {response_data.heart ? <>
            <div className="message_action_subheader">Heart</div>
            <div className="message_text">{response_data.heart}</div>
          </> : null}
          {response_data.abdomen ? <>
            <div className="message_action_subheader">Abdomen</div>
            <div className="message_text">{response_data.abdomen}</div>
          </> : null}
          {response_data.extremities ? <>
            <div className="message_action_subheader">Extremities</div>
            <div className="message_text">{response_data.extremities}</div>
          </> : null}
          {response_data.lymph_nodes ? <>
            <div className="message_action_subheader">Lymph nodes</div>
            <div className="message_text">{response_data.lymph_nodes}</div>
          </> : null}
          {response_data.skin ? <>
            <div className="message_action_subheader">Skin</div>
            <div className="message_text">{response_data.skin}</div>
          </> : null}
          {response_data.neuro ? <>
            <div className="message_action_subheader">Neuro</div>
            <div className="message_text">{response_data.neuro}</div>
          </> : null}
          {response_data.other ? <>
            <div className="message_action_subheader">Other</div>
            <div className="message_text">{response_data.other}</div>
          </> : null}
        </> : null}
        {(action_type == "physical_exam_test" || action_type == "diagnostics") ? (
          response_data ? (
            response_data.results.length > 0 ? (
              response_data.results.map((r, index) => (
                <MessageResult action_type={action_type} key={index} result={r} files={files} handleUpdateMessage={handleUpdateMessage} message_appear={message_appear} message_id={message_id} index={index}/>
              ))
            ) : (
              <div className="message_text">{`Please try a different ${action_type == "diagnostics" ? "diagnostic" : "physical exam test"}.`}</div>
            )
          ) : <>
            <div className="message_text">{response_text}</div>
          </>
        ) : null}
      </div>
    </div>
  )
}

function MessageResult({ action_type, result, files, handleUpdateMessage, message_id, index, message_appear }) {
  const [is_submitting, setIsSubmitting] = useState(false);

  function submitInterpretation({ formatted_data, passthrough_args, focusError, updateData }) {
    setIsSubmitting(passthrough_args?.i_dont_know ? "idk" : true);

    let learner_interpretation = passthrough_args?.i_dont_know ? "I don't know" : formatted_data.learner_interpretation;

    if (passthrough_args?.i_dont_know) {
      updateData(draft => {
        draft.learner_interpretation = "I don't know";
      });
    }

    let response_data = {
      results: {
        [index]: {
          learner_interpretation,
        }
      }
    }

    handleUpdateMessage({ message_id, response_data }).then((data) => {

      setIsSubmitting(false);

    }).catch((error) => {
      console.error(error);
      setIsSubmitting(false);
      focusError({name: "learner_interpretation", error: "Something went wrong, please try again."});
    })
  }

  return (
    <>
      <div className="message_action_subheader">{result.name}</div>
      {result.images?.length > 0 ?
        <>
          <MessageImages images={result.images} files={files}/>
          {result.learner_interpret || result.correct_interpretation ? <Spacing/> : null}
          {result.learner_interpret ? <>
            <FormProvider onSubmit={submitInterpretation} readOnly={(result.learner_interpretation ? true : false) || !handleUpdateMessage} initial_data={{learner_interpretation: result.learner_interpretation}}>
              <FormDisabled readOnly={is_submitting && true}>
                <Textbox name="learner_interpretation" title={`What's your interpretation of the image${result.images?.length > 1 ? "s" : ""}?`} placeholder="" required={"Please provide your interpretation"} multiline={true} autofocus={index == 0}/>
                {(!result.learner_interpretation && handleUpdateMessage) ? <>
                  <Spacing/>
                  <FormSubmitProvider>
                    {({handleSubmit}) => (
                      <div className="buttons right">
                        <FormDataProvider fields={["learner_interpretation"]}>
                          {({data}) => (
                            <Button text="I don't know" className="hollow" onClick={() => {handleSubmit({validate: false, passthrough_args: {i_dont_know: true}})}} loading={is_submitting === "idk"} disabled={(data.learner_interpretation?.trim().length > 0 && data.learner_interpretation !== "I don't know")}/>
                          )}
                        </FormDataProvider>
                        <Button text="Submit" className="primary" onClick={handleSubmit} loading={is_submitting === true}/>
                      </div>
                    )}
                  </FormSubmitProvider>
                </> : null}
              </FormDisabled>
            </FormProvider>
          </> : null}
          {((result.learner_interpret ? result.learner_interpretation : true) && result.correct_interpretation) ? <>
            <div className={"correct_interpretation" + ((message_appear && result.learner_interpret) ? " correct_interpretation_animate" : "")}>
              {result.learner_interpret ? <ThreeQuarterSpacing/> : null}
              <div className="message_action_subheader">{action_type == "diagnostics" ? "Final report" : "Attending's assessment"}</div>
              <div className="message_text">{result.result}</div>
            </div>
          </> : null}
        </>
      :
        <div className="message_text">{result.result}</div>
      }
    </>
  )
}

function MessageImages({images, files}) {
  return (
    <div className={images.length > 1 ? "message_images_multiple" : "message_images_single"}>
      {images.map(file => (
        <MessageImage key={file.file_id} file={files.find(f => f._id == file.file_id)} single={images.length == 1}/>
      ))}
    </div>
  )
}

function MessageImage({file, single}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [openImageModal] = useModal(({ closeModal }) => {
    return (
      <Modal image={true}>
        <FileImage file={file} onClick={closeModal}/>
      </Modal>
    )
  });

  return (
    <div className={"message_image_item" + (single ? " single" : "")} style={single ? {aspectRatio: file.aspect_ratio} : {}} onClick={openImageModal} tabIndex={0} role='button' aria-label='Open image'>
      <FileImage className="message_image_image" file={file} onLoad={() => setImageLoaded(true)} onError={() => setLoadError(true)}/>
      {(!imageLoaded && !loadError) ? <div className="message_image_spinner"><svg className="spinner" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><circle className="path" fill="none" strokeWidth="10" strokeLinecap="butt" cx="50" cy="50" r="40"></circle></svg></div> : null}
      {loadError ? <div className="message_image_error"><Icon name="alert-circle-outline"/></div> : null}
    </div>
  )
}

function Message({message, message_appear, review, handleUpdateMessage}) {
  if (message.action_type) {
    return (
      <>
        <ActionTo action_type={message.action_type} action_data={message.action_data} message_appear={message_appear} review={review} />
        {(message.response_text || message.response_data) ? <ActionFrom action_type={message.action_type} action_data={message.action_data} response_text={message.response_text} response_data={message.response_data} files={message.files} message_appear={message_appear} handleUpdateMessage={handleUpdateMessage} message_id={message._id} /> : null}
        {message.error_retry_fn ? <MessageFailedRetry kind="action" error_retry_fn={message.error_retry_fn}/> : null}
        {message.action_hint ? <MessageActionHint kind="action" text={message.action_hint.text} icon={message.action_hint.icon} button={message.action_hint.button}/> : null}
      </>
    )
  } else {
    return (
      <>
        {message.text ? <MessageBubble direction="to" text={message.text} message_appear={message_appear} voice={message.voice} /> : null}
        {message.response_text ? <MessageBubble direction="from" text={message.response_text} message_appear={message_appear} voice={message.voice} /> : null}
        {message.error_retry_fn ? <MessageFailedRetry kind="message" error_retry_fn={message.error_retry_fn}/> : null}
      </>
    )
  }
}

function MessageFailedRetry({ kind, error_retry_fn }) {
  return (
    <div className={`after_message_hint message_failed_retry ${kind == "action" ? "center" : ""}`}>
      <div>
        <div>
          <Icon name="alert-circle"/>
          <div>Message failed to send</div>
        </div>
        <div>
          <Button text="Try again" className="small hollow" onClick={error_retry_fn}/>
        </div>
      </div>
    </div>
  )
}

function MessageActionHint({ kind, icon, text, button }) {
  return (
    <div className={`after_message_hint ${kind == "action" ? "center" : ""}`}>
      <div>
        {text ? <div>
          {icon ? <Icon name={icon}/> : null}
          <div>{text}</div>
        </div> : null}
        {button ? <div>
          {button}
        </div> : null}
      </div>
    </div>
  )
}

export function MessageItem({item, message_appear, review, handleUpdateMessage}) {
  if (item.type == "opening_blurb") {
    return (
      <div className="conversation_message_opening_info">
        <div className="message_action_subheader">Case scenario</div>
        <div className="message_text">{item.opening_blurb}</div>
        {item.workup_to_date_summary ? <>
          <div className="message_action_subheader">Workup done to date</div>
          <div className="message_text">{item.workup_to_date_summary}</div>
        </> : null}
      </div>
    )
  } else if (item.type == "add_differential") {
    return (
      <div className="conversation_message_opening_info">
        <div className="message_action_header">
          <div>
            <Icon name="plus-circle" style={{fill: "var(--add_differential_green)"}}/>
          </div>
          <div>Added differential</div>
        </div>
        <div className="message_text">{item.name}</div>
        <div className="message_action_subheader">Why?</div>
        <div className="message_text">{item.why}</div>
      </div>
    )
  } else if (item.type == "rule_out_differential") {
    return (
      <div className="conversation_message_opening_info">
        <div className="message_action_header">
          <div>
            <Icon name="close-circle" style={{fill: "var(--rule_out_differential_red)"}}/>
          </div>
          <div>Ruled out differential</div>
        </div>
        <div className="message_text">{item.name}</div>
        <div className="message_action_subheader">Why?</div>
        <div className="message_text">{item.why}</div>
      </div>
    )
  } else if (item.type == "rename_differential") {
    return (
      <div className="conversation_message_opening_info">
        <div className="message_action_header">
          <div>
            <Icon name="pencil-circle" style={{fill: "var(--rename_differential_blue)"}}/>
          </div>
          <div>Changed differential</div>
        </div>
        <div className="message_text"><span style={{textDecoration: "line-through", color: "var(--btext)"}}>{item.old_name}</span><div style={{paddingTop: "4px"}}></div>{item.new_name}</div>
        {item.why ? <>
          <div className="message_action_subheader">Why?</div>
          <div className="message_text">{item.why}</div>
        </> : null}
      </div>
    )
  } else {
    return (
      <Message message={item.message} message_appear={message_appear} review={review} handleUpdateMessage={handleUpdateMessage}/>
    )
  }
}

function Diagnoses({visible, considering_differentials}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="diagnoses">
      <Header title="Diagnoses"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <DiagnosesAndDifferentialsForm visible={visible} considering_differentials={considering_differentials}/>
        </div>
      </div>
    </div>
  )
}

export function DiagnosesAndDifferentialsForm({visible = false, considering_differentials}) {
  const { updateData } = useFormData([]);

  const [openMoveToRuledOutModal] = useModal(({ context, closeModal }) => {
    function moveToRuledOut() {
      closeModal();
      context.move_to_ruled_out();
    }

    function justRemove() {
      closeModal();
      context.remove();
    }

    return (
      <Modal title="Move to ruled-out differentials?">
        <ModalContent>
          You can move this to the ruled-out differentials list to show that you considered it but ruled it out.
        </ModalContent>
        <ModalButtons>
          <Button text="Just remove" onClick={justRemove}/>
          <Button className="primary" text="Move to ruled-out" onClick={moveToRuledOut}/>
        </ModalButtons>
      </Modal>
    )
  })

  function handleRemoveDifferential({ index, removeItem, differential_name }) {
    if (considering_differentials.find((differential) => differential.name == differential_name && !differential.ruled_out)) {
      openMoveToRuledOutModal({
        move_to_ruled_out: () => {
          removeItem();
          updateData((draft) => {
            let new_item = {differential: differential_name, justification: null, why: null};
            if (Object.values(draft.differentials_ruled_out).length == 1 && !(Object.values(draft.differentials_ruled_out)[0]?.differential.trim()?.length > 0 || Object.values(draft.differentials_ruled_out)[0].justification?.trim()?.length > 0)) {
              draft.differentials_ruled_out[Object.keys(draft.differentials_ruled_out)[0]] = new_item;
            } else {
              let new_id = String(Date.now());
              draft.differentials_ruled_out[new_id] = new_item;
            }
          });
        },
        remove: () => {
          removeItem();
        }
      });
    } else {
      removeItem();
    }
  }

  return (
    <>
      <InlineHeader title="Diagnoses"/>
      <InlineSection>
        <FormArrayGroup name="diagnoses" start_with_one={true} autofocus_first_entry={true} add_button_label="Add another diagnosis" required="Please add at least one diagnosis">
          {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
            <div className="diagnosis_item differential_item">
              <FormDataProvider fields={["diagnosis", "justification"]}>{({data}) => (
                <>
                  <div>
                    <div>
                      <Textbox name="diagnosis" title="Diagnosis" placeholder="" required={(data.diagnosis?.trim()?.length > 0 || data.justification?.trim()?.length > 0 || index == 0) ? "Please enter a diagnosis" : false} autofocus={just_added && visible}/>
                      {(!readOnly && index > 0) ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                    </div>
                  </div>
                  <div style={{display: "none"}}>
                    <Textbox name="why" title="What made you consider this?" placeholder="" required={false} multiline={true}/>
                  </div>
                  <div>
                    <Textbox name="justification" title="Supporting evidence" placeholder="" required={(data.diagnosis?.trim()?.length > 0 || data.justification?.trim()?.length > 0 || index == 0) ? "Please explain your diagnosis" : false} multiline={true}/>
                  </div>
                </>
              )}</FormDataProvider>
            </div>
          )}
        </FormArrayGroup>
      </InlineSection>
      <InlineHeader title="Differential diagnoses"/>
      <InlineSection>
        <FormDataProvider fields={["differentials"]}>{({data, readOnly}) => {
          if (Object.values(data?.differentials || {}).length == 0 && readOnly) {
            return <div>(None)</div>
          } else {
            return null;
          }
        }}</FormDataProvider>
        <FormArrayGroup name="differentials" start_with_one={true} add_button_label="Add another differential">
          {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
            <div className="diagnosis_item differential_item">
              <FormDataProvider fields={["differential", "justification", "probability_rating"]}>{({data}) => (
                <>
                  <div>
                    <div>
                      <Textbox name="differential" title="Differential" placeholder="" required={(data.differential?.trim()?.length > 0 || data.justification?.trim()?.length > 0 || data.probability_rating) ? "Please enter a differential" : false} autofocus={just_added && visible}/>
                      {!readOnly ? <div className="textbox_remove_item" onClick={() => {handleRemoveDifferential({index, removeItem, differential_name: data.differential?.trim()})}} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                    </div>
                  </div>
                  <div className="diagnosis_probability">
                    <Dropdown name="probability_rating" title="Probability" required={(data.differential?.trim()?.length > 0 || data.justification?.trim()?.length > 0) ? "Please choose" : false}>
                      <DropdownOption name="high" text="High"/>
                      <DropdownOption name="medium" text="Medium"/>
                      <DropdownOption name="low" text="Low"/>
                    </Dropdown>
                  </div>
                  <div style={{display: "none"}}>
                    <Textbox name="why" title="What made you consider this?" placeholder="" required={false} multiline={true}/>
                  </div>
                  <div>
                    <Textbox name="justification" title="Explain your probability rating" placeholder="" required={(data.differential?.trim()?.length > 0 || data.justification?.trim()?.length > 0) ? "Please explain your probability rating" : false} multiline={true}/>
                  </div>
                </>
              )}</FormDataProvider>
            </div>
          )}
        </FormArrayGroup>
      </InlineSection>
      <InlineHeader title="Ruled-out differentials"/>
      <InlineSection>
        <FormDataProvider fields={["differentials_ruled_out"]}>{({data, readOnly}) => {
          if (Object.values(data?.differentials_ruled_out || {}).length == 0 && readOnly) {
            return <div>(None)</div>
          } else {
            return null;
          }
        }}</FormDataProvider>
        <FormArrayGroup name="differentials_ruled_out" start_with_one={true} add_button_label="Add another ruled-out differential">
          {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
            <div className="diagnosis_item differential_item">
              <FormDataProvider fields={["differential", "justification"]}>{({data}) => (
                <>
                  <div>
                    <div>
                      <Textbox name="differential" title="Differential" placeholder="" required={(data.differential?.trim()?.length > 0 || data.justification?.trim()?.length > 0) ? "Please enter a differential" : false} autofocus={just_added && visible}/>
                      {!readOnly ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                    </div>
                  </div>
                  <div style={{display: "none"}}>
                    <Textbox name="why" title="What made you consider this?" placeholder="" required={false} multiline={true}/>
                  </div>
                  <div>
                    <Textbox name="justification" title="How did you rule this out?" placeholder="" required={(data.differential?.trim().length > 0 || data.justification?.trim().length > 0) ? "Please explain how you ruled this out" : false} multiline={true}/>
                  </div>
                </>
              )}</FormDataProvider>
            </div>
          )}
        </FormArrayGroup>
      </InlineSection>
    </>
  )
}

function TreatmentPlan({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="treatment_plan">
      <Header title="Assessment and plan"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <FormDataProvider fields={["diagnoses", "differentials", "differentials_ruled_out"]}>{({data}) => {
            let diagnoses = Object.values(data.diagnoses || {}).filter(a => a.diagnosis?.trim().length > 0).map(a => a.diagnosis);
            let differentials = Object.values(data.differentials || {}).filter(a => a.differential?.trim().length > 0).map(a => a.differential);
            let ruled_out_differentials = Object.values(data.differentials_ruled_out || {}).filter(a => a.differential?.trim().length > 0).map(a => a.differential);
            return (
              <div className="treatmen_plan_diagnoses_hint">
                <div>
                  <label>{diagnoses.length > 1 ? "Diagnoses" : "Diagnosis"}</label>
                  <ul>
                    {diagnoses.length > 0 ? diagnoses.map((diagnosis, index) => (
                      <li key={index}>{diagnosis}</li>
                    )) : <li style={{color: "var(--ctext)"}}>(None)</li>}
                  </ul>
                </div>
                <div>
                  <label>Differentials</label>
                  <ul>
                    {differentials.length > 0 ? differentials.map((differential, index) => (
                      <li key={index}>{differential}</li>
                    )) : <li style={{color: "var(--ctext)"}}>(None)</li>}
                  </ul>
                </div>
                <div>
                  <label>Ruled-out differentials</label>
                  <ul>
                    {ruled_out_differentials.length > 0 ? ruled_out_differentials.map((differential, index) => (
                      <li key={index}>{differential}</li>
                    )) : <li style={{color: "var(--ctext)"}}>(None)</li>}
                  </ul>
                </div>
              </div>
            )
          }}</FormDataProvider>
          <Textbox name="treatment_plan" title="Assessment and plan" placeholder="" required="Please enter your assessment and plan" markdown={true} fullWidth={true} multiline={true} hint={"Describe your assessment with supporting evidence, then outline treatments, referrals, follow-ups, and recommendations."} autofocus={visible}/>
        </div>
      </div>
    </div>
  )
}

function FollowUpQuestions({visible, scenario_data}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="questions">
      <Header title="Follow-up questions"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <TextboxGroup name="questions" textboxes={scenario_data.questions.map(a => {return {title: a}})} multiline={true} required="This question is required" autofocus={visible}/>
        </div>
      </div>
    </div>
  )
}

export function SaveTranscriptButton({ conversation_message_items, session_id }) {
  let text = conversation_message_items.map((item) => {
    if (item.type == "opening_blurb") {
      return "Opening blurb: "+item.opening_blurb;
    } else if (item.type == "message") {
      if (item.message.action_type == "diagnostics") {
        return `**DIAGNOSTIC ORDER**
Name: ${item.message.action_data.name}
Expecting to find: ${item.message.action_data.justification}
${item.message.action_data.related_differentials ? `Related differentials: ${item.message.action_data.related_differentials.map(a => a.name).join(", ")}` : ""}
Result:
${item.message.response_text || item.message.response_data?.results.map(a => a.name+":\n"+a.result).join("\n") || ""}`
      } else if (item.message.action_type == "physical_exam_routine") {
        return `**PERFORMED THE ROUTINE PHYSICAL EXAM**
Expecting to find: ${item.message.action_data.justification}
${item.message.action_data.working_diagnosis ? `Working diagnosis: ${item.message.action_data.working_diagnosis.name}` : ""}`
      } else if (item.message.action_type == "physical_exam_test") {
        return `**PHYSICAL EXAM TEST**
Name: ${item.message.action_data.name}
Expecting to find: ${item.message.action_data.justification}
${item.message.action_data.related_differentials ? `Related differentials: ${item.message.action_data.related_differentials.map(a => a.name).join(", ")}` : ""}
Result:
${item.message.response_text || item.message.response_data?.results.map(a => a.name+":\n"+a.result).join("\n") || ""}`
      } else {
        return "Student: "+item.message.text+"\n\nPatient: "+(item.message.response_text || "");
      }
    } else if (item.type == "add_differential") {
      return `**ADDED DIFFERENTIAL**
Differential: ${item.name}
Reasoning: ${item.why || "N/A"}`
    } else if (item.type == "rule_out_differential") {
      return `**RULED OUT DIFFERENTIAL**
Differential: ${item.name}
Reasoning: ${item.why || "N/A"}`
    } else if (item.type == "rename_differential") {
      return `**RENAMED DIFFERENTIAL**
Old name: ${item.old_name}
New name: ${item.new_name}
Reasoning: ${item.why || "N/A"}`
    }
  }).join("\n\n");

  const [openDownloadModal] = useCopyPromptModal({ title: "Transcript (patient only, not advice actors)", text: text, filename: "transcript-"+session_id });

  return (
    <Button text="Save transcript" icon="file-download-outline" onClick={openDownloadModal}/>
  )
}